// Ol
import { Feature } from "ol";
import Point from "ol/geom/Point";
import LineString from "ol/geom/LineString";
import Polygon from "ol/geom/Polygon";
import Geometry from "ol/geom/Geometry";
import MultiPoint from "ol/geom/MultiPoint";
import MultiPolygon from "ol/geom/MultiPolygon";
import MultiLineString from "ol/geom/MultiLineString";

// Types
import { DCRecord } from '@/@types/lib/dataController';

import { generateDXFPoints, generateDXFLinesPolygons } from '@/lib/dxfGenerator';

interface ExportDxfOptions {
  records: DCRecord[];
  filename: string;
}

type Point3D = [number, number, number];
type Line3D = Point3D[];
type DXFItem = { data: string; suffix: string };

function to3D(coord: number[]): Point3D {
  return [coord[0], coord[1], coord[2] ?? 0];
}

export default function exportFeaturesToDxf({ records, filename }: ExportDxfOptions) {
  if (!records?.length) return;

  const points: Point3D[] = [];
  const lines: Line3D[] = [];
  const polygons: Line3D[] = [];

  records.forEach((rec) => {
    const olGeom = (rec.geom as Feature<Geometry>).getGeometry();
    if (!olGeom) return;
    const geom = olGeom.clone().transform("EPSG:3857", "EPSG:3765");

    if (geom instanceof Point) {
      points.push(to3D(geom.getCoordinates()));
    } else if (geom instanceof MultiPoint) {
      const coordsPoint = geom.getCoordinates();
      coordsPoint.forEach((pt) => points.push(to3D(pt)));
    } else if (geom instanceof LineString) {
      const coordsLine = geom.getCoordinates();
      const line: Line3D = coordsLine.map(to3D);
      lines.push(line);
    } else if (geom instanceof Polygon || geom instanceof MultiLineString) {
      const allLines = geom.getCoordinates();
      allLines.forEach((coordsLine) => {
        const line: Line3D = coordsLine.map(to3D);
        if (geom instanceof Polygon) polygons.push(line);
        else lines.push(line)
      });
    } else if (geom instanceof MultiPolygon) {
      const allPolygons = geom.getCoordinates();
      allPolygons.forEach((polygon) => {
        polygon.forEach((lineCoords) => {
          const line: Line3D = lineCoords.map(to3D);
          polygons.push(line);
        });
      });
    } else {
      console.warn("Unsupported geometry type for DXF export", geom);
    }
  });

  const dxfItems: DXFItem[] = [];

  if (points.length) dxfItems.push({ data: generateDXFPoints(points), suffix: "points" });
  if (lines.length) dxfItems.push({ data: generateDXFLinesPolygons(lines), suffix: "lines" });
  if (polygons.length) dxfItems.push({ data: generateDXFLinesPolygons(polygons), suffix: "polygons" });

  dxfItems.forEach(({ data, suffix }) => {
    const blob = new Blob([data], { type: "application/dxf;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${filename}_${suffix}.dxf`;
    link.click();
    URL.revokeObjectURL(url);
  });
}